import type RecordService from '../record.service';
import type ExemplarService from '../exemplar.service';
import {Kind, SearchType} from 'shared/constants/portaro.constants';
import {createSearchManagerBackedPageableList} from '../../search/search-manager/search-manager';
import {DefaultReactivePageableList} from 'shared/utils/default-reactive-pageable-list';
import type {Document, Loan, Rec, RecordOperation, SearchParams} from 'typings/portaro.be.types';
import type {PageableList} from 'shared/utils/reactive-pageable-list';
import type {EmptyObject} from 'typings/portaro.fe.types';
import type {SearchManagerBuilderFactoryService} from '../../search/search-manager/search-manager-factory.service';

export class DocumentDetailTabsetService {
    public static serviceName = 'documentDetailTabsetService';

    /*@ngInject*/
    constructor(private recordService: RecordService,
                private exemplarService: ExemplarService,
                private searchManagerBuilderFactoryService: SearchManagerBuilderFactoryService) {
    }

    public createPartsPageableList(record: Rec): PageableList<Document, string, EmptyObject> {
        return DefaultReactivePageableList.createWithoutLoadListSizeFunction({
            loadListItems: (pageable) => this.recordService.getAllRecordParts(record, pageable.pageNumber, pageable.pageSize),
            pageSize: 10
        });
    }

    public createArticlesPageableList(record: Rec): PageableList<Document, string, EmptyObject> {
        return DefaultReactivePageableList.createWithoutLoadListSizeFunction({
            loadListItems: (pageable) => this.recordService.getAllRecordArticles(record, pageable.pageNumber, pageable.pageSize),
            pageSize: 10
        });
    }

    public createLoanPageableList(record: Rec): PageableList<Loan, string, EmptyObject> {
        const staticParams = {
            type: SearchType.TYPE_LOAN_SEARCH,
            kind: [Kind.KIND_LOAN],
            record: [record.id],
            loanState: ['ON_WAITING_LIST', 'UNSENT_RESERVATION', 'SENT_RESERVATION', 'UNPROCESSED_ORDER', 'PROCESSED_ORDER', 'LENT', 'RETURNED', 'CANCELLED_RESERVATION'],
            pageSize: 10
        };
        const searchManager = this.searchManagerBuilderFactoryService
            .createBuilder<SearchParams, Loan>()
            .withStaticParams(staticParams)
            .createLocalSearch();
        return createSearchManagerBackedPageableList(searchManager);
    }

    public createOperationPageableList(record: Rec): PageableList<RecordOperation, string, EmptyObject> {
        const staticParams = {
            type: SearchType.TYPE_RECORD_OPERATION_SEARCH,
            kind: [Kind.KIND_RECORD_OPERATION],
            record: [record.id],
            pageSize: 10
        };
        const searchManager = this.searchManagerBuilderFactoryService
            .createBuilder<SearchParams, RecordOperation>()
            .withStaticParams(staticParams)
            .createLocalSearch();
        return createSearchManagerBackedPageableList(searchManager);
    }

    public async loadRevisions(record: Rec) {
        return this.recordService.getAllRecordRevisions(record);
    };

    public async createExemplar(document: Document) {
        await this.exemplarService.createExemplarByFormAndReload(document, null);
    }
}